export const CHECK_IN_PUBLIC_DATA_KEY = 'publicMoorageCheckIn';
export const CHECK_INS_PUBLIC_DATA_KEY = 'publicMoorageCheckIns';
export const CHECK_IN_PRIVATE_DATA_KEY = 'publicMoorageCheckIn';
export const CHECK_IN_HISTORY_PRIVATE_DATA_KEY = 'publicMoorageCheckIns';

export const isListingPublicCheckInDisabled = () =>
  ['development', 'test'].includes(process.env.REACT_APP_ENV);

export const getPublicMoorageCheckIn = listing =>
  isListingPublicCheckInDisabled()
    ? null
    : listing?.attributes?.publicData?.[CHECK_IN_PUBLIC_DATA_KEY] || null;

export const getPublicMoorageCheckIns = listing => {
  if (isListingPublicCheckInDisabled()) return [];
  const checkIns = listing?.attributes?.publicData?.[CHECK_INS_PUBLIC_DATA_KEY];
  return Array.isArray(checkIns) ? checkIns : [];
};

const getListingId = listing => {
  const uuid = listing?.id?.uuid;
  if (typeof uuid === 'string' && uuid.length > 0) return uuid;
  const id = listing?.id;
  return typeof id === 'string' && id.length > 0 ? id : null;
};

export const getCurrentUserPublicMoorageCheckIn = currentUser =>
  currentUser?.attributes?.profile?.privateData?.[CHECK_IN_PRIVATE_DATA_KEY] || null;

export const getCurrentUserPublicMoorageCheckIns = currentUser => {
  const checkIns =
    currentUser?.attributes?.profile?.privateData?.[CHECK_IN_HISTORY_PRIVATE_DATA_KEY];
  return Array.isArray(checkIns) ? checkIns : [];
};

export const isCheckInActive = checkIn => {
  if (!checkIn?.expiresAt) return false;
  if (checkIn?.endedAt) return false;
  if (checkIn?.state && checkIn.state !== 'Checked In') return false;
  if (checkIn?.status && !['active', 'Checked In'].includes(checkIn.status)) return false;
  const expiresAt = Date.parse(checkIn.expiresAt);
  return Number.isFinite(expiresAt) && expiresAt > Date.now();
};

const getCheckInSortTime = checkIn =>
  Date.parse(
    checkIn?.arrivalTime || checkIn?.checkedInAt || checkIn?.startTime || checkIn?.createdAt || 0
  ) || 0;

/**
 * Read the active check-in snapshot from an own listing.
 *
 * Public listing pages intentionally ignore listing-level check-in data in development and test
 * environments. The owner listings page is different: it receives own listings and needs the
 * host-facing snapshot without issuing a request for every row.
 */
export const getActiveListingPublicMoorageCheckIn = listing => {
  const publicData = listing?.attributes?.publicData || {};
  const history = publicData[CHECK_INS_PUBLIC_DATA_KEY];
  const activePointer = publicData[CHECK_IN_PUBLIC_DATA_KEY];
  const recordsById = new Map();

  (Array.isArray(history) ? history : []).forEach(checkIn => {
    if (checkIn?.id) recordsById.set(checkIn.id, checkIn);
  });
  if (activePointer?.id) recordsById.set(activePointer.id, activePointer);

  return (
    Array.from(recordsById.values())
      .filter(isCheckInActive)
      .sort((a, b) => getCheckInSortTime(b) - getCheckInSortTime(a))[0] || null
  );
};

export const getActivePublicMoorageCheckIn = (listing, currentUser) => {
  const listingId = getListingId(listing);
  const currentUserCheckIn = getCurrentUserPublicMoorageCheckIn(currentUser);
  if (
    listingId &&
    currentUserCheckIn?.listingId === listingId &&
    isCheckInActive(currentUserCheckIn)
  ) {
    return currentUserCheckIn;
  }

  return isListingPublicCheckInDisabled() ? null : getActiveListingPublicMoorageCheckIn(listing);
};

export const getCheckInAgeHours = checkIn => {
  if (!checkIn?.checkedInAt) return null;
  const checkedInAt = Date.parse(checkIn.checkedInAt);
  if (!Number.isFinite(checkedInAt)) return null;
  const diffHours = Math.floor((Date.now() - checkedInAt) / (1000 * 60 * 60));
  return Math.max(1, diffHours);
};

export const getCheckInAge = checkIn => {
  const startTime = checkIn?.startTime || checkIn?.checkedInAt;
  if (!startTime) return null;
  const checkedInAt = Date.parse(startTime);
  if (!Number.isFinite(checkedInAt)) return null;
  const diffMs = Math.max(0, Date.now() - checkedInAt);
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  if (diffMinutes < 60) {
    return { value: diffMinutes, unit: 'min' };
  }
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  return { value: diffHours, unit: 'hr' };
};

export const formatCheckInAge = checkIn => {
  const age = getCheckInAge(checkIn);
  if (!age) return '0 min';
  const unitLabel = age.unit === 'hr' && age.value !== 1 ? 'hrs' : age.unit;
  return `${age.value} ${unitLabel}`;
};
